import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', nativeName: 'English', flag: '🇺🇸' },
  ko: { name: 'Korean', nativeName: '한국어', flag: '🇰🇷' }
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export interface I18nConfig {
  fallbackLng: SupportedLanguage;
  supportedLngs: SupportedLanguage[];
  defaultNS: string;
  ns: string[];
  interpolation: {
    escapeValue: boolean;
  };
  detection: {
    order: string[];
    caches: string[];
  };
  backend: {
    loadPath: string;
  };
}

const i18nConfig: I18nConfig = {
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: ['en', 'ko'],
  defaultNS: 'translation',
  // Every namespace that any component reaches for via `useTranslation` MUST be
  // listed here. `useSuspense` defaults to true, so a namespace that is missing
  // from this list is fetched lazily on first render — and if that render is
  // triggered by a synchronous update (a click, a route change), React aborts
  // with "A component suspended while responding to synchronous input" and
  // blanks the screen. Keep this in sync with public/locales/<lng>/*.json.
  ns: ['translation', 'common', 'auth', 'campaigns', 'navigation', 'dashboard', 'public', 'admin', 'matches', 'profile', 'search', 'terms', 'privacy'],
  interpolation: {
    escapeValue: false // React already escapes values
  },
  detection: {
    order: ['localStorage', 'navigator', 'htmlTag'],
    caches: ['localStorage']
  },
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json'
  }
};

// Development mode configuration
const DEV_MODE_ENABLED = process.env.NODE_ENV === 'development';
const SHOW_TRANSLATION_KEYS = localStorage.getItem('i18n_show_keys') === 'true';
const HIGHLIGHT_MISSING = localStorage.getItem('i18n_highlight_missing') === 'true';

/**
 * Enable/disable translation key display mode
 * Usage in browser console: window.i18nShowKeys(true)
 */
if (typeof window !== 'undefined') {
  (window as any).i18nShowKeys = (enabled: boolean) => {
    localStorage.setItem('i18n_show_keys', String(enabled));
    console.log(`Translation key display mode: ${enabled ? 'ENABLED' : 'DISABLED'}`);
    console.log('Reload the page to see changes.');
  };

  (window as any).i18nHighlightMissing = (enabled: boolean) => {
    localStorage.setItem('i18n_highlight_missing', String(enabled));
    console.log(`Missing translation highlighting: ${enabled ? 'ENABLED' : 'DISABLED'}`);
    console.log('Reload the page to see changes.');
  };

  // Log available dev tools
  if (DEV_MODE_ENABLED) {
    console.log('%c[i18n] Development Tools Available:', 'color: #3b82f6; font-weight: bold;');
    console.log('  window.i18nShowKeys(true/false) - Toggle translation key display');
    console.log('  window.i18nHighlightMissing(true/false) - Toggle missing translation highlighting');
  }
}

export async function initializeI18n(): Promise<typeof i18n> {
  await i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      ...i18nConfig,
      // Ensure fallback to English for missing keys
      fallbackLng: DEFAULT_LANGUAGE,
      // Return key if translation is missing (last resort)
      returnNull: false,
      returnEmptyString: false,
      // Development mode warnings for missing translations
      saveMissing: DEV_MODE_ENABLED,
      missingKeyHandler: (lngs, ns, key) => {
        if (DEV_MODE_ENABLED) {
          console.warn(
            `%c[i18n] Missing translation key:%c "${key}" %cin namespace%c "${ns}" %cfor languages:%c ${lngs.join(', ')}`,
            'color: #ef4444; font-weight: bold;',
            'color: #f59e0b;',
            'color: #6b7280;',
            'color: #f59e0b;',
            'color: #6b7280;',
            'color: #3b82f6;'
          );
        }
      },
      // Custom formatting for missing keys.
      //
      // i18next calls this with (key, defaultValue) where defaultValue is the
      // inline fallback from `t('some.key', '기본값')` — it is `undefined` when
      // the caller didn't supply one. We MUST return it when present, otherwise
      // every inline fallback in the app silently degrades to the raw key
      // (e.g. "users.detail.matchStatus.not_selected" leaking into the UI).
      parseMissingKeyHandler: (key, defaultValue) => {
        // Explicit key-display debug mode always wins.
        if (SHOW_TRANSLATION_KEYS) {
          return `[KEY: ${key}]`;
        }
        // Honour the caller's inline fallback. `missingKeyHandler` above still
        // logs the missing key in development, so nothing is hidden.
        if (typeof defaultValue === 'string' && defaultValue !== '') {
          return defaultValue;
        }
        if (DEV_MODE_ENABLED && HIGHLIGHT_MISSING) {
          return `⚠️ ${key}`;
        }
        if (DEV_MODE_ENABLED) {
          return `[MISSING: ${key}]`;
        }
        return key;
      }
    });

  // Add CSS for missing translation highlighting in development mode
  if (DEV_MODE_ENABLED && HIGHLIGHT_MISSING && typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
      /* i18n missing translation highlighting */
      [data-i18n-missing="true"] {
        background-color: rgba(239, 68, 68, 0.1) !important;
        border: 1px dashed #ef4444 !important;
        padding: 2px 4px !important;
        border-radius: 2px !important;
      }
      [data-i18n-missing="true"]::before {
        content: "⚠️ ";
        color: #ef4444;
      }
    `;
    document.head.appendChild(style);
  }

  // Add CSS for translation key display mode
  if (SHOW_TRANSLATION_KEYS && typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
      /* i18n translation key display */
      [data-i18n-key]::after {
        content: " [" attr(data-i18n-key) "]";
        color: #3b82f6;
        font-size: 0.75em;
        font-weight: normal;
        opacity: 0.7;
        font-family: monospace;
      }
    `;
    document.head.appendChild(style);
  }

  return i18n;
}

export default i18n;
